import * as THREE from 'three';
import { ShaderMaterial } from 'three';
import {
  ColorMap,
  intColorFromHexString,
  intColorMapFromStringColorMap,
  defaultVisibilityColorMap,
} from './ColorMap';
import { PointsAttributeBuffer } from './PointsAttributeBuffer';
import { defaultMaxId } from './Loaders/BinPointsLoader';
import { EDLShadeRenderer } from './EyeDomeLighting/edlShadeRenderer';

export enum PointCloudMaterialMode {
  RGB = 0,
  ANNOTATION_MAP_1D = 1,
  ANNOTATION_MAP_2D = 2,
  PICKING_ID = 3,
}

const DEFAULT_ENABLE_SIZE_ATTENUATION = true;
export const POINT_ATTENUATION_SCALE = 0.5 * 0.1;

// const DEFAULT_VBOX_MULTIPLY = 0.2;
// const DEFAULT_VBOX_ADD_COLOR = 0x202020;
// const DEFAULT_VBOX_FINAL_ALPHA = 1;

const modeDefines = `
#define MODE_RGB 0
#define MODE_ANNOTATION_MAP_1D 1
#define MODE_ANNOTATION_MAP_2D 2
#define MODE_PICKING_ID 3
`;

const combinedVertexShader = `
${modeDefines}

uniform float size;
uniform float scale;
uniform float screenScale;

uniform mat4 vboxMatrix;
uniform mat4 smallVboxMatrix;

//uniform vec3 visibilityBoxMin;
//uniform vec3 visibilityBoxMax;

varying float isVisible;
varying float hasSolidColor;

// float insideBox3D(vec3 v, vec3 bboxMin, vec3 bboxMax) {
//   vec3 s = step(bboxMin, v) - step(bboxMax, v);
//   return s.x * s.y * s.z;
// }

float isInsideBox3D(vec4 v, mat4 boxMat) {
  vec4 localV = boxMat * v;
  vec3 s = step(vec3(-0.5,-0.5,-0.5), localV.xyz) - step(vec3(0.5,0.5,0.5), localV.xyz);
  return s.x * s.y * s.z;
}

attribute vec2 visibilityUV;
varying vec2 vVisibilityUV;

#if HAS_COLOR == 1
attribute vec3 color;
varying vec3 vColor;
#endif

#if MATERIAL_MODE == MODE_RGB

#elif MATERIAL_MODE == MODE_ANNOTATION_MAP_1D
attribute float annotationIdU;
varying float vAnnotationIdU;

#elif MATERIAL_MODE == MODE_ANNOTATION_MAP_2D
attribute vec2 annotationIdUV;
varying vec2 vAnnotationIdUV;

#elif MATERIAL_MODE == MODE_PICKING_ID
attribute vec4 pickingId;
varying vec4 vPickingId;

#endif

void main() {

  vVisibilityUV = visibilityUV;

#if HAS_COLOR == 1
  vColor = color;
#endif

#if MATERIAL_MODE == MODE_RGB

#elif MATERIAL_MODE == MODE_ANNOTATION_MAP_1D
  vAnnotationIdU = annotationIdU;

#elif MATERIAL_MODE == MODE_ANNOTATION_MAP_2D
  vAnnotationIdUV = annotationIdUV;

#elif MATERIAL_MODE == MODE_PICKING_ID
  vPickingId = pickingId;

#endif

  vec3 transformed = vec3( position );
  vec4 mvPosition = vec4( transformed, 1.0 );
  vec4 mvWorldPosition = modelMatrix * mvPosition;

  hasSolidColor = isInsideBox3D(mvWorldPosition, smallVboxMatrix);
  isVisible = isInsideBox3D(mvWorldPosition, vboxMatrix);

  mvPosition = modelViewMatrix * mvPosition;

  gl_Position = projectionMatrix * mvPosition;

#if ENABLE_POINT_SIZE_ATTENUATION == 1
  // using 10mm * point_size as physical size
  gl_PointSize = clamp( 2.0 * atan(0.01 * size * 0.5 / length(mvPosition.xyz)) * scale * screenScale, 0.25, 40.0);
#else
  gl_PointSize = size;
#endif
}
`;

const combinedFragmentShader = `
${modeDefines}

uniform vec3 diffuse;
uniform float opacity;

// greyish area outside small vbox
uniform vec3 vboxAdditiveColor;
uniform vec2 vboxMultiplyAndAlpha;

varying float isVisible;
varying float hasSolidColor;

uniform sampler2D visibilityTexture;
varying vec2 vVisibilityUV;

#if HAS_COLOR == 1
varying vec3 vColor;
#endif

#if HAS_EDL_SHADES == 1
uniform sampler2D edlShade0;
uniform sampler2D edlShade1;
uniform sampler2D edlShade2;
uniform sampler2D edlDepth;
uniform vec2 screenSize;
uniform int enableEDL;
#endif

#if MATERIAL_MODE == MODE_RGB

#elif MATERIAL_MODE == MODE_ANNOTATION_MAP_1D
uniform sampler2D annotationTexture;
varying float vAnnotationIdU;

#elif MATERIAL_MODE == MODE_ANNOTATION_MAP_2D
uniform sampler2D annotationTexture;
varying vec2 vAnnotationIdUV;

#elif MATERIAL_MODE == MODE_PICKING_ID
varying vec4 vPickingId;

#endif

void main() {

  if (isVisible < 0.999999) {
    discard;
  }

  vec4 visibilityColor = texture2D(visibilityTexture, vVisibilityUV);
  if (visibilityColor[3] == 0.0) {
    discard;
  }

#if MATERIAL_MODE == MODE_PICKING_ID
  gl_FragColor = vPickingId;
  return;

#else
  float alpha = 1.0;

  #if MATERIAL_MODE == MODE_RGB
    vec4 color = vec4( vColor, 1.0 );

  #elif MATERIAL_MODE == MODE_ANNOTATION_MAP_1D
    vec4 color = texture2D(annotationTexture, vec2(vAnnotationIdU, 0.5));
    alpha = color.a;

  #elif MATERIAL_MODE == MODE_ANNOTATION_MAP_2D
    vec4 color = texture2D(annotationTexture, vAnnotationIdUV);
    alpha = color.a;

  #else
    raise_an_ERROR
  #endif

  #if HAS_COLOR == 1 && MATERIAL_MODE != MODE_RGB
    color = vec4(mix(vColor, color.rgb, color.a), 1.0);
    alpha = 1.0;
  #endif

  #if HAS_EDL_SHADES
  if (enableEDL == 1) {
    vec2 screenUV = gl_FragCoord.xy / screenSize.xy;
    vec3 shade0 = texture2D(edlShade0, screenUV).rgb;
    float depth = texture2D(edlDepth, screenUV).r;
    if (depth > 0.999) {
      color =  vec4(shade0, alpha);
    }else{
      vec3 shade1 = texture2D(edlShade1, screenUV).rgb;
      vec3 shade2 = texture2D(edlShade2, screenUV).rgb;
      vec3 shade = (shade0 + 0.5 * shade1 + 0.25 * shade2) / (1.0 + 0.5 + 0.25);
      color = vec4(shade, alpha);
    }
  }
  #endif

  if (hasSolidColor < 0.999999) {
    color.rgb = color.rgb * vboxMultiplyAndAlpha.x + vboxAdditiveColor;
    color.a = vboxMultiplyAndAlpha.y;
  }

  gl_FragColor = color;

#endif // MODE_PICKING_ID

  #include <colorspace_fragment>
}
`;

type MakeCombinedDefinesProps = {
  materialMode: PointCloudMaterialMode;
  hasVisibilityUV: boolean;
  hasColor: boolean;
  hasEDLShades: boolean;
  enableSizeAttenuation: boolean;
};
const makeCombinedDefines = ({
  materialMode,
  hasVisibilityUV,
  hasColor,
  hasEDLShades,
  enableSizeAttenuation,
}: MakeCombinedDefinesProps) => {
  return `
#define MATERIAL_MODE ${materialMode}
#define HAS_VISIBILITY_UV ${hasVisibilityUV ? 1 : 0}
#define HAS_COLOR ${hasColor ? 1 : 0}
#define HAS_EDL_SHADES ${hasEDLShades ? 1 : 0}
#define ENABLE_POINT_SIZE_ATTENUATION ${enableSizeAttenuation ? 1 : 0}
`;
};

export type VisibilityBoxUniforms = {
  smallMatrix: THREE.Matrix4;
  smallMatrixInverse: THREE.Matrix4;
  matrix: THREE.Matrix4;
  matrixInverse: THREE.Matrix4;
  colorMultiplyAndAlpha: THREE.Vector2;
  additiveColor: THREE.Vector3;
};
export const makeVisibilityBoxUniforms = (): VisibilityBoxUniforms => ({
  smallMatrix: new THREE.Matrix4(),
  smallMatrixInverse: new THREE.Matrix4(),
  matrix: new THREE.Matrix4(),
  matrixInverse: new THREE.Matrix4(),
  colorMultiplyAndAlpha: new THREE.Vector2(),
  additiveColor: new THREE.Vector3(),
});

type MakeCombinedUniformsProps = {
  vboxUniforms: VisibilityBoxUniforms;
  edlRenderer?: EDLShadeRenderer;
  pointSize?: number;
  scale?: number;
  visibilityColorMap?: ColorMap;
};
const makeCombinedUniforms = ({
  vboxUniforms,
  edlRenderer,
  pointSize,
  scale,
  visibilityColorMap,
}: MakeCombinedUniformsProps) => {
  const visibilityTextureElement:
    | object
    | { visibilityTexture: { type: string; value: THREE.DataTexture } } =
    visibilityColorMap === undefined
      ? {}
      : { visibilityTexture: { type: 't', value: visibilityColorMap.texture } };

  const edlShadesElement:
    | object
    | {
        edlShade0: { type: string; value: THREE.DataTexture };
        edlShade1: { type: string; value: THREE.DataTexture };
        edlShade2: { type: string; value: THREE.DataTexture };
        screenSize: { type: string; value: THREE.Vector2 };
        enableEDL: { type: string; value: number };
      } =
    edlRenderer === undefined
      ? {}
      : {
          edlShade0: { type: 't', value: edlRenderer.targets[0].texture },
          edlShade1: { type: 't', value: edlRenderer.targets[1].texture },
          edlShade2: { type: 't', value: edlRenderer.targets[2].texture },
          screenSize: { type: 'vec2', value: new THREE.Vector2(1, 1) },
          enableEDL: { type: 'i', value: 0 },
        };

  return {
    size: { type: 'f', value: pointSize ?? 1 },
    scale: { type: 'f', value: scale ?? 1 },
    screenScale: { type: 'f', value: 1 },
    vboxMatrix: { type: 'mat4', value: vboxUniforms.matrixInverse },
    smallVboxMatrix: { type: 'mat4', value: vboxUniforms.smallMatrixInverse },
    vboxMultiplyAndAlpha: { type: 'vec2', value: vboxUniforms.colorMultiplyAndAlpha },
    vboxAdditiveColor: { type: 'vec3', value: vboxUniforms.additiveColor },
    ...visibilityTextureElement,
    ...edlShadesElement,
  };
};

type MakeShaderMaterialProps = {
  props: THREE.ShaderMaterialParameters;
  defines: string;
};
const makeShaderMaterial = ({ props, defines }: MakeShaderMaterialProps) => {
  const { vertexShader, fragmentShader } = props;
  const shaderMaterial = new ShaderMaterial({
    ...props,
    vertexShader: defines + vertexShader,
    fragmentShader: defines + fragmentShader,
  });
  shaderMaterial.defaultAttributeValues = { color: [1, 1, 1], visibilityUV: [0, 0] };
  return shaderMaterial;
};

type CreateColorMapMaterialProps = {
  vboxUniforms: VisibilityBoxUniforms;
  edlRenderer: EDLShadeRenderer;
  colorMap: ColorMap;
  pointSize?: number;
  scale?: number;
  enableSizeAttenuation?: boolean;
  visibilityColorMap?: ColorMap;
  hasColor?: boolean;
};
export const createColorMapMaterial = ({
  vboxUniforms,
  edlRenderer,
  colorMap,
  pointSize,
  scale,
  enableSizeAttenuation = DEFAULT_ENABLE_SIZE_ATTENUATION,
  visibilityColorMap,
  hasColor = false,
}: CreateColorMapMaterialProps) => {
  const { texture, cardinality } = colorMap;

  const materialMode =
    cardinality === 1
      ? PointCloudMaterialMode.ANNOTATION_MAP_1D
      : PointCloudMaterialMode.ANNOTATION_MAP_2D;

  const defines = makeCombinedDefines({
    materialMode,
    hasVisibilityUV: visibilityColorMap !== undefined,
    hasColor,
    hasEDLShades: true,
    enableSizeAttenuation,
  });

  const uniforms = {
    ...makeCombinedUniforms({ vboxUniforms, edlRenderer, pointSize, scale, visibilityColorMap }),
    annotationTexture: { type: 't', value: texture },
  };

  return makeShaderMaterial({
    props: {
      uniforms,
      vertexShader: combinedVertexShader,
      fragmentShader: combinedFragmentShader,
      blending: THREE.NormalBlending,
      transparent: true,
    },
    defines,
  });
};

type CreateVertexColorMaterialProps = {
  vboxUniforms: VisibilityBoxUniforms;
  edlRenderer: EDLShadeRenderer;
  pointSize?: number;
  scale?: number;
  enableSizeAttenuation?: boolean;
  visibilityColorMap?: ColorMap;
};
export const createVertexColorMaterial = ({
  vboxUniforms,
  edlRenderer,
  pointSize,
  scale,
  enableSizeAttenuation = DEFAULT_ENABLE_SIZE_ATTENUATION,
  visibilityColorMap,
}: CreateVertexColorMaterialProps) => {
  const defines = makeCombinedDefines({
    materialMode: PointCloudMaterialMode.RGB,
    hasVisibilityUV: visibilityColorMap !== undefined,
    hasColor: true,
    hasEDLShades: true,
    enableSizeAttenuation,
  });

  const uniforms = makeCombinedUniforms({
    vboxUniforms,
    edlRenderer,
    pointSize,
    scale,
    visibilityColorMap,
  });

  return makeShaderMaterial({
    props: {
      uniforms,
      vertexShader: combinedVertexShader,
      fragmentShader: combinedFragmentShader,
      blending: THREE.NormalBlending,
      transparent: true,
    },
    defines,
  });
};

type CreatePickingMaterialProps = {
  vboxUniforms: VisibilityBoxUniforms;
  pointSize?: number;
  scale?: number;
  enableSizeAttenuation?: boolean;
  visibilityColorMap?: ColorMap;
};
export const createPickingMaterial = ({
  vboxUniforms,
  pointSize,
  scale,
  enableSizeAttenuation = DEFAULT_ENABLE_SIZE_ATTENUATION,
  visibilityColorMap,
}: CreatePickingMaterialProps) => {
  const defines = makeCombinedDefines({
    materialMode: PointCloudMaterialMode.PICKING_ID,
    hasVisibilityUV: visibilityColorMap !== undefined,
    hasColor: false,
    hasEDLShades: false,
    enableSizeAttenuation,
  });

  const uniforms = makeCombinedUniforms({
    vboxUniforms,
    pointSize,
    scale,
    visibilityColorMap,
  });

  return makeShaderMaterial({
    props: {
      uniforms,
      vertexShader: combinedVertexShader,
      fragmentShader: combinedFragmentShader,
      blending: THREE.NoBlending,
      transparent: true,
    },
    defines,
  });
};

export const calcScale = (screenHeight: number, cameraFov: number): number => {
  const fovRadians = (cameraFov * Math.PI) / 180;
  const scale = screenHeight / fovRadians;
  return scale;
};

export interface PointCloudMaterialProps {
  // Color is defined as four component hex string: "#rrggbbaa" in Map<number, string>
  // Map<number, string> is deprecated, use ColorMap instead
  colorMap?: Map<number, string> | ColorMap;
  visibilityMap?: ColorMap;
  visibilityMapForPicking?: ColorMap;
  // defaultColor is deprecated, use ColorMap instead
  defaultColor?: string;
  pointSize?: number;
  enableSizeAttenuation?: boolean;
  attributeName: string;
  visibilityAttributeName?: string;
  // true -> attribute contains rgb colors; false|undefined -> attribute contains ids
  isColor?: boolean;
  // true -> should blend rgb colors with annotation color
  hasColor?: boolean;
  allowInconsistentMaxId?: boolean;
}

export interface PointCloudMaterialExtendedProps extends PointCloudMaterialProps {
  invalidateFrame: () => void;
}

export class PointCloudMaterial {
  public shaderMaterial: ShaderMaterial;

  public pickingShaderMaterial: ShaderMaterial;

  public attributeName: string;

  public visibilityAttributeName?: string;

  public colorMap?: ColorMap;

  public visibilityMap: ColorMap;

  public visibilityMapForPicking: ColorMap;

  public isColor: boolean;

  public hasColor: boolean;

  public allowInconsistentMaxId: boolean;

  constructor(
    props: PointCloudMaterialExtendedProps,
    vboxUniforms: VisibilityBoxUniforms,
    edlRenderer: EDLShadeRenderer,
    scale: number
  ) {
    const {
      colorMap,
      visibilityMap,
      visibilityMapForPicking,
      defaultColor,
      pointSize,
      enableSizeAttenuation,
      attributeName,
      visibilityAttributeName,
      isColor,
      hasColor,
      allowInconsistentMaxId,
      invalidateFrame,
    } = props;

    this.visibilityAttributeName = visibilityAttributeName;
    this.attributeName = attributeName;
    this.isColor = isColor ?? false;
    this.hasColor = hasColor ?? false;
    this.allowInconsistentMaxId = allowInconsistentMaxId ?? false;

    if (this.isColor) {
      this.visibilityMap = visibilityMap ?? defaultVisibilityColorMap;
      this.visibilityMapForPicking = visibilityMapForPicking ?? this.visibilityMap;
      this.shaderMaterial = createVertexColorMaterial({
        vboxUniforms,
        edlRenderer,
        pointSize,
        scale,
        enableSizeAttenuation,
        visibilityColorMap: this.visibilityMap,
      });
    } else {
      this.colorMap =
        colorMap instanceof ColorMap
          ? colorMap
          : new ColorMap(
              defaultMaxId,
              intColorMapFromStringColorMap(colorMap),
              intColorFromHexString(defaultColor)
            );

      if (!visibilityMap) {
        this.visibilityMap = this.colorMap;
        this.visibilityAttributeName = attributeName;
      } else {
        this.visibilityMap = visibilityMap;
      }
      this.visibilityMapForPicking = visibilityMapForPicking ?? this.visibilityMap;

      this.shaderMaterial = createColorMapMaterial({
        vboxUniforms,
        edlRenderer,
        colorMap: this.colorMap,
        pointSize,
        scale,
        enableSizeAttenuation,
        visibilityColorMap: this.visibilityMap,
        hasColor: this.hasColor ?? false,
      });
    }

    this.pickingShaderMaterial = createPickingMaterial({
      vboxUniforms,
      pointSize,
      scale,
      enableSizeAttenuation,
      visibilityColorMap: this.visibilityMapForPicking,
    });

    if (invalidateFrame) {
      this.colorMap?.setInvalidateFrame(invalidateFrame);
      this.visibilityMap?.setInvalidateFrame(invalidateFrame);
    }
  }

  setScale(scale: number) {
    this.shaderMaterial.uniforms.scale.value = scale;
    this.shaderMaterial.uniformsNeedUpdate = true;
    this.pickingShaderMaterial.uniforms.scale.value = scale;
    this.pickingShaderMaterial.uniformsNeedUpdate = true;
  }

  visibilityBoxNeedsUpdate(vboxUniforms: VisibilityBoxUniforms) {
    [this.shaderMaterial.uniforms, this.pickingShaderMaterial.uniforms].forEach((uniforms) => {
      uniforms.vboxMatrix.value = vboxUniforms.matrixInverse;
      uniforms.smallVboxMatrix.value = vboxUniforms.smallMatrixInverse;
      uniforms.vboxMultiplyAndAlpha.value = vboxUniforms.colorMultiplyAndAlpha;
      uniforms.vboxAdditiveColor.value = vboxUniforms.additiveColor;
    });

    this.shaderMaterial.uniformsNeedUpdate = true;
    this.pickingShaderMaterial.uniformsNeedUpdate = true;
  }

  public get shaderAttributeName() {
    return this.isColor
      ? 'color'
      : this.colorMap?.cardinality === 1
        ? 'annotationIdU'
        : 'annotationIdUV';
  }

  public get shaderVisibilityAttributeName() {
    return 'visibilityUV';
  }

  // raises an exception to avoid binding an incompatible data buffer with material
  public assertCompatible(
    attributeName: string,
    attributeBuffer: PointsAttributeBuffer | undefined
  ) {
    if (attributeBuffer === undefined || this.isColor) {
      return;
    }
    const materialMaxId =
      attributeName === this.visibilityAttributeName
        ? this.visibilityMap.maxId
        : this.colorMap?.maxId;
    if (!this.allowInconsistentMaxId && materialMaxId !== attributeBuffer.maxId) {
      throw new Error(
        `Material '${this.attributeName}' attribute '${attributeName}' maxId=${materialMaxId} is different than data buffer maxId=${attributeBuffer.maxId}`
      );
    }
  }

  public dispose() {
    this.shaderMaterial.dispose();
  }
}
